import type { ComputerUseStatus } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, LoaderCircle, Monitor, Settings, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import { loadComputerUse } from '@/lib/computer-use';
import { errorMessage } from '@/lib/error-message';
import { useRouter } from '@/lib/navigation';

/** Where Settings › Computer use opens. */
export const COMPUTER_USE_SETTINGS = '/settings?section=computer-use';

function State({ ok, children }: { ok: boolean | null; children: ReactNode }) {
  return (
    <li className="type-ui flex items-center gap-2.5 px-3 py-2">
      <span
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full',
          ok === true ? 'bg-success/15 text-success' : 'bg-control text-muted-foreground',
        )}
      >
        {ok === true ? <Check className="size-3" /> : <X className="size-3" />}
      </span>
      {children}
    </li>
  );
}

/**
 * The Computer tool (D39), on this computer only: whether the agents may use the Mac's apps
 * (Cua Driver), what macOS allows it and which apps, with Settings › Computer use to change it.
 * The Mac's screen itself is not streamed into the panel yet: the agents see it through Cua
 * Driver, the user on the Mac.
 */
export function ComputerView({
  load = loadComputerUse,
}: {
  /** Reads the state of computer use (the local server's `/v1/computer`). */
  load?: () => Promise<ComputerUseStatus>;
}) {
  const router = useRouter();
  const [status, setStatus] = useState<ComputerUseStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    load().then(
      (next) => active && setStatus(next),
      (reason: unknown) => active && setError(errorMessage(reason)),
    );
    return () => {
      active = false;
    };
  }, [load]);
  const settings = (
    <Button
      className="rounded-control"
      onClick={() => router.push(COMPUTER_USE_SETTINGS)}
      size="sm"
    >
      <Settings />
      Settings › Computer use
    </Button>
  );
  if (error !== null)
    return (
      <div className="flex flex-col items-center gap-2">
        <PanelEmpty icon={Monitor} text={error} title="Computer use is not available" />
        {settings}
      </div>
    );
  if (status === null)
    return (
      <p className="type-meta flex items-center gap-2 text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" />
        Reading computer use…
      </p>
    );
  if (!status.supported)
    return (
      <PanelEmpty
        icon={Monitor}
        text="Agents use the apps of a Mac only."
        title="Not on this computer"
      />
    );
  return (
    <div className="desktop-panel-view-enter flex h-full min-h-0 flex-col gap-3">
      <div className="type-meta flex items-center gap-2 text-muted-foreground">
        <Monitor className="size-4" />
        <span className="min-w-0 flex-1 truncate">
          This Mac ·{' '}
          {status.installed === null
            ? 'Cua Driver not installed'
            : `Cua Driver ${status.installed}`}
        </span>
        <span
          className={cn(
            'rounded-sm px-2 py-0.5',
            status.enabled ? 'bg-running/15 text-running' : 'bg-control text-muted-foreground',
          )}
        >
          {status.enabled ? 'Agents may use it' : 'Off'}
        </span>
      </div>
      <ul aria-label="Computer use" className="rounded-2xl border bg-card py-1">
        <State ok={status.installed !== null}>Cua Driver installed</State>
        <State ok={status.permissions?.accessibility ?? null}>Accessibility</State>
        <State ok={status.permissions?.screenRecording ?? null}>Screen recording</State>
        <State ok={status.enabled}>Agents may use the apps of this Mac</State>
      </ul>
      {status.apps.length > 0 && (
        <section aria-label="Allowed apps" className="flex flex-col gap-1.5">
          <h3 className="type-meta text-muted-foreground">Allowed apps</h3>
          <ul className="flex flex-wrap gap-2">
            {status.apps.map((app) => (
              <li
                className="type-meta flex items-center gap-1.5 rounded-control bg-control px-2.5 py-1"
                key={app.bundleId}
              >
                {app.icon !== null && <img alt="" className="size-4" src={app.icon} />}
                {app.name}
              </li>
            ))}
          </ul>
        </section>
      )}
      {status.error !== null && <p className="type-meta text-destructive">{status.error}</p>}
      <div className="mt-auto flex items-center gap-2">
        {settings}
        <span className="type-caption ml-auto text-muted-foreground">Only on this computer</span>
      </div>
    </div>
  );
}
